import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Button,
  InputNumber,
  Spin,
  Empty,
  message,
  Space,
  Tag,
  Checkbox,
  Input,
  Alert,
} from 'antd';
import {
  DeleteOutlined,
  MinusOutlined,
  PlusOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  GiftOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { cartService, storageUrl } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './CustomerDashboard.css';

const { Title, Text } = Typography;

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price || 0);
};

const Cart = () => {
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(null);
  const [updating, setUpdating] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const response = await cartService.getCart();
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const items = cart?.items || cart?.data?.items || [];

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setUpdating((prev) => ({ ...prev, [itemId]: true }));
    try {
      await cartService.updateCartItem(itemId, newQuantity);
      fetchCart();
    } catch (error) {
      message.error('Failed to update cart');
    } finally {
      setUpdating((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleRemoveItem = async (itemId) => {
    setUpdating((prev) => ({ ...prev, [itemId]: true }));
    try {
      await cartService.removeFromCart(itemId);
      message.success('Item removed from cart');
      setSelectedIds((prev) => prev.filter((id) => id !== itemId));
      fetchCart();
    } catch (error) {
      message.error('Failed to remove item');
    } finally {
      setUpdating((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleCheckout = (itemIds) => {
    const ids = itemIds || selectedIds;
    if (ids.length > 0) {
      navigate(`/checkout?items=${ids.join(',')}`);
    } else {
      navigate('/checkout');
    }
  };

  const handleBuyNow = (item) => {
    handleCheckout([item.id]);
  };

  const getPrice = (item) => {
    const p = parseFloat(item.price_at_add);
    if (!isNaN(p)) return p;
    const p2 = parseFloat(item.price);
    if (!isNaN(p2)) return p2;
    return parseFloat(item.product_service?.price) || 0;
  };

  const updateQuantity = items.reduce((sum, item) => {
    return sum + getPrice(item) * (item.quantity || 1);
  }, 0);

  const selectedItems =
    selectedIds.length > 0 ? items.filter((item) => selectedIds.includes(item.id)) : items;

  const selectedTotal = selectedItems.reduce((sum, item) => {
    return sum + getPrice(item) * (item.quantity || 1);
  }, 0);

  if (loading) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="customer-page" style={{ padding: '0' }}>
        <Card className="customer-card">
          <Empty description="Your cart is empty" image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button className="customer-btn-primary" onClick={() => navigate('/customer/products')}>
              Browse Products
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  return (
    <div className="customer-page" style={{ padding: '0', maxWidth: 1100, margin: '0 auto' }}>
      <div className="customer-page-header">
        <Title level={2}>
          Shopping Cart ({items.length} {items.length === 1 ? 'item' : 'items'})
        </Title>
      </div>

      {/* Order Summary — leads at top so total + checkout are visible without scrolling */}
      <div style={{ position: 'sticky', top: 16, zIndex: 5, marginBottom: 16 }}>
        <Card
          className="customer-card"
          style={{ borderRadius: 16, border: '1px solid #e8f0e8' }}
          styles={{ body: { padding: '20px 24px' } }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <div>
              <Text style={{ color: '#6b7280', fontSize: 14 }}>
                {selectedIds.length > 0
                  ? `${selectedIds.length} of ${items.length} items selected`
                  : `${items.length} ${items.length === 1 ? 'item' : 'items'} in cart`}
              </Text>
              {couponDiscount > 0 && (
                <Text style={{ color: '#f1a134', fontSize: 12, fontWeight: 600, display: 'block' }}>
                  Coupon discount: -{formatPrice(couponDiscount)}
                </Text>
              )}
              <div style={{ fontSize: 24, fontWeight: 700, color: '#2d5a3d', marginTop: 4 }}>
                {selectedIds.length > 0
                  ? formatPrice(Math.max(0, selectedTotal - couponDiscount))
                  : formatPrice(Math.max(0, updateQuantity - couponDiscount))}
              </div>
            </div>
            <Space wrap>
              <Button size="large" onClick={() => navigate('/customer/products')}>
                Continue Shopping
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                onClick={() => handleCheckout()}
                style={{
                  height: 48,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                {selectedIds.length > 0
                  ? `Checkout (${selectedIds.length})`
                  : 'Proceed to Checkout'}
              </Button>
            </Space>
          </div>
        </Card>
      </div>

      {/* Coupon Section */}
      <Card
        className="customer-card"
        style={{ marginBottom: 16, border: '1px solid #e8f0e8', borderRadius: 16 }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <GiftOutlined style={{ fontSize: 20, color: '#52b788' }} />
          <Text strong style={{ color: '#2d5a3d' }}>
            Have a coupon?
          </Text>
          <Input
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => {
              setCouponCode(e.target.value.toUpperCase());
              setCouponError(null);
            }}
            style={{ width: 200, borderRadius: 8 }}
            size="middle"
          />
          <Button
            type="primary"
            loading={applyingCoupon}
            onClick={async () => {
              if (!couponCode.trim()) return;
              setApplyingCoupon(true);
              setCouponError(null);
              setCouponMsg(null);
              try {
                const res = await cartService.applyCoupon(couponCode.trim());
                const data = res.data;
                if (data.discount_amount) {
                  const disc =
                    typeof data.discount_amount === 'object'
                      ? data.discount_amount.total_discount ||
                        data.discount_amount.product_discount ||
                        0
                      : data.discount_amount;
                  setCouponDiscount(Number(disc) || 0);
                  setCouponMsg(data.message || 'Coupon applied!');
                } else {
                  setCouponError('Coupon could not be applied');
                }
                fetchCart();
              } catch (err) {
                const msg = err.response?.data?.message || 'Invalid coupon code';
                setCouponError(msg);
                setCouponDiscount(0);
              } finally {
                setApplyingCoupon(false);
              }
            }}
            style={{ borderRadius: 8, background: '#52b788', borderColor: '#52b788' }}
          >
            Apply
          </Button>
          {couponDiscount > 0 && (
            <Button
              icon={<CloseCircleOutlined />}
              onClick={() => {
                setCouponDiscount(0);
                setCouponCode('');
                setCouponMsg(null);
              }}
              size="small"
              danger
            >
              Remove
            </Button>
          )}
        </div>
        {couponMsg && (
          <Alert
            message={couponMsg}
            type="success"
            showIcon
            style={{ marginTop: 8, borderRadius: 8 }}
            closable
            onClose={() => setCouponMsg(null)}
          />
        )}
        {couponError && (
          <Alert
            message={couponError}
            type="error"
            showIcon
            style={{ marginTop: 8, borderRadius: 8 }}
            closable
            onClose={() => setCouponError(null)}
          />
        )}
      </Card>

      <Card
        className="customer-card"
        style={{ borderRadius: 16, overflow: 'hidden' }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafff5', borderBottom: '2px solid #e8f0e8' }}>
                <th style={{ padding: '14px 12px', width: 40 }}>
                  <Checkbox
                    checked={selectedIds.length === items.length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < items.length}
                    onChange={(e) => setSelectedIds(e.target.checked ? items.map((i) => i.id) : [])}
                  />
                </th>
                <th
                  style={{
                    padding: '14px 12px',
                    textAlign: 'left',
                    color: '#374151',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Product
                </th>
                <th
                  style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    color: '#374151',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Price
                </th>
                <th
                  style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    color: '#374151',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Quantity
                </th>
                <th
                  style={{
                    padding: '14px 12px',
                    textAlign: 'right',
                    color: '#374151',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Total
                </th>
                <th style={{ padding: '14px 12px', width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const itemPrice = (() => {
                  const p = parseFloat(item.price_at_add);
                  if (!isNaN(p)) return p;
                  const p2 = parseFloat(item.price);
                  if (!isNaN(p2)) return p2;
                  return parseFloat(item.product_service?.price) || 0;
                })();
                const isService = item.product_service?.type === 'service';
                return (
                  <tr
                    key={item.id}
                    style={{
                      borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background: selectedIds.includes(item.id) ? '#fafff5' : '#fff',
                    }}
                  >
                    <td style={{ padding: '16px 12px' }}>
                      <Checkbox
                        checked={selectedIds.includes(item.id)}
                        onChange={(e) => {
                          setSelectedIds((prev) =>
                            e.target.checked
                              ? [...prev, item.id]
                              : prev.filter((id) => id !== item.id),
                          );
                        }}
                      />
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <img
                          src={
                            storageUrl(
                              item.product_service?.images?.[0]?.url ||
                                item.product_service?.images?.[0]?.image_url,
                            ) ||
                            `https://placehold.co/80x80?text=${encodeURIComponent(item.product_service?.name || 'Item')}`
                          }
                          alt={item.product_service?.name}
                          onClick={() => navigate(`/products/${item.product_service_id}`)}
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: 8,
                            objectFit: 'cover',
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                        />
                        <div>
                          <Text
                            strong
                            style={{
                              color: '#2d5a3d',
                              fontSize: 15,
                              display: 'block',
                              cursor: 'pointer',
                            }}
                            onClick={() => navigate(`/products/${item.product_service_id}`)}
                          >
                            {item.product_service?.name || 'Product'}
                          </Text>
                          {isService && (
                            <Tag color="blue" style={{ marginTop: 4 }}>
                              Service
                            </Tag>
                          )}
                          {itemPrice === 0 && (
                            <Tag color="gold" style={{ marginTop: 4 }}>
                              Free for Members
                            </Tag>
                          )}
                          {item.booking_date && (
                            <div style={{ marginTop: 4 }}>
                              <CalendarOutlined style={{ color: '#52b788', marginRight: 4 }} />
                              <Text style={{ color: '#52b788', fontSize: 12 }}>
                                {item.booking_date}
                                {item.booking_time ? ` at ${item.booking_time}` : ''}
                              </Text>
                            </div>
                          )}
                          <Button
                            type="link"
                            size="small"
                            icon={<ShoppingOutlined />}
                            onClick={() => handleBuyNow(item)}
                            style={{ padding: 0, marginTop: 6, height: 'auto', color: '#52b788' }}
                          >
                            Buy Now
                          </Button>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <Text style={{ color: '#374151', fontWeight: 500 }}>
                        {itemPrice === 0 ? 'Free' : formatPrice(itemPrice)}
                      </Text>
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Space>
                          <Button
                            size="small"
                            icon={<MinusOutlined />}
                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1 || updating[item.id]}
                            loading={updating[item.id]}
                            style={{ width: 26, height: 26 }}
                          />
                          <InputNumber
                            size="small"
                            min={1}
                            max={99}
                            value={item.quantity}
                            onChange={(value) => handleUpdateQuantity(item.id, value)}
                            style={{ width: 42, textAlign: 'center' }}
                          />
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                            loading={updating[item.id]}
                            style={{ width: 26, height: 26 }}
                          />
                        </Space>
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'right' }}>
                      <Text strong style={{ color: '#2d5a3d', fontSize: 15 }}>
                        {itemPrice === 0 ? 'Free' : formatPrice(itemPrice * item.quantity)}
                      </Text>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveItem(item.id)}
                        loading={updating[item.id]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Cart;
